import React, { forwardRef } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Box } from '@mui/material';

import { consoleTokens } from './consoleTokens';

export interface FilterTokenProps {
  /** The dimension being filtered, rendered dim: `period:`, `basis:`. */
  tokenKey: string;
  /** Current value, rendered ink: `May 16–31`, `posted`. */
  value: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Editable filter token — the console's replacement for form controls.
 * Renders `key: value ▾`; the host opens a popover/menu anchored to it.
 */
export const FilterToken = forwardRef<HTMLButtonElement, FilterTokenProps>(
  function FilterToken({ tokenKey, value, onClick, disabled, ...rest }, ref) {
    return (
      <Box
        component="button"
        type="button"
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        aria-label={rest['aria-label'] ?? `Change ${tokenKey}`}
        sx={{
          alignItems: 'center',
          bgcolor: consoleTokens.tokenBg,
          border: `1px solid ${consoleTokens.line}`,
          borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer',
          display: 'inline-flex',
          flexShrink: 0,
          fontFamily: consoleTokens.mono,
          fontSize: '0.75rem',
          gap: 0.25,
          height: 26,
          whiteSpace: 'nowrap',
          opacity: disabled ? 0.6 : 1,
          px: 1,
          '&:hover': disabled
            ? undefined
            : {
                bgcolor: consoleTokens.tokenHoverBg,
                borderColor: consoleTokens.tokenHoverBorder,
              },
        }}
      >
        <Box component="span" sx={{ color: consoleTokens.dim }}>
          {tokenKey}:
        </Box>
        <Box component="span" sx={{ color: consoleTokens.ink, fontWeight: 500 }}>
          {value}
        </Box>
        <ArrowDropDownIcon
          sx={{ color: consoleTokens.fainter, fontSize: 16, mr: -0.5 }}
        />
      </Box>
    );
  },
);
